import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import ProcessSheetTable from '../ProcessSheetTable';
import LogoToggle from '../LogoToggle';
import { getBondByLabel, computeSheet } from '../../../utils/calculations';

const STATUS_COLORS = {
    Saved:           'bg-blue-100 text-blue-700',
    'In Production': 'bg-amber-100 text-amber-700',
    Completed:       'bg-emerald-100 text-emerald-700',
};

const fmt = (dateStr) => dateStr
    ? new Date(dateStr).toLocaleString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    })
    : '—';

// ── Human-readable history entry ─────────────────────────────────────────────
const HistoryEntry = ({ entry, index, total }) => {
    const [expanded, setExpanded] = useState(false);
    const changes = entry.changes || {};

    // Flatten nested changes object into readable { label, oldVal, newVal } lines
    const lines = [];

    const addLine = (label, oldVal, newVal) => {
        if (String(oldVal) === String(newVal)) return; // skip no-ops
        lines.push({ label, oldVal, newVal });
    };

    Object.entries(changes).forEach(([key, val]) => {
        if (key === 'inputs' && typeof val === 'object' && !Array.isArray(val)) {
            // Nested per-field input diffs
            Object.entries(val).forEach(([field, diff]) => {
                if (diff?.oldValue !== undefined || diff?.newValue !== undefined) {
                    addLine(field, diff.oldValue, diff.newValue);
                }
            });
        } else if (key === 'materialPlan') {
            const items = Array.isArray(val?.newValue) ? val.newValue : null;
            lines.push({
                label: 'Material plan recorded',
                note: items ? `${items.length} item${items.length !== 1 ? 's' : ''}` : '',
            });
        } else if (key === 'client') {
            addLine('Client', val?.oldValue, val?.newValue);
        } else if (key === 'status') {
            addLine('Status', val?.oldValue, val?.newValue);
        } else if (key === 'quantity') {
            addLine('Quantity', val?.oldValue, val?.newValue);
        } else if (key === 'reason') {
            // Inventory deduction reason string — show as a note
            lines.push({ label: 'Reason', note: String(val) });
        }
    });

    const isLast = index === total - 1;

    return (
        <div className={`relative pl-6 pb-5 ${!isLast ? 'border-l-2 border-gray-100' : ''}`}>
            {/* Timeline dot */}
            <span className="absolute -left-[5px] top-1 w-2.5 h-2.5 rounded-full bg-gray-300 border-2 border-white block" />

            {/* Row header */}
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <span className="text-xs font-semibold text-gray-700">{entry.updatedBy || 'System'}</span>
                    <span className="text-xs text-gray-400 ml-2">{fmt(entry.updatedAt)}</span>
                </div>
                {lines.length > 0 && (
                    <button
                        onClick={() => setExpanded(p => !p)}
                        className="text-xs text-blue-500 hover:text-blue-700 flex-shrink-0 transition-colors"
                    >
                        {expanded ? 'Hide' : `${lines.length} change${lines.length !== 1 ? 's' : ''}`}
                    </button>
                )}
            </div>

            {/* Change details */}
            {expanded && (
                <div className="mt-2 space-y-1">
                    {lines.map((line, i) => (
                        <div key={i} className="text-xs bg-gray-50 border border-gray-100 rounded px-3 py-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            <span className="font-mono text-gray-600 font-medium min-w-0 break-all">{line.label}</span>
                            {line.note !== undefined ? (
                                <span className="text-gray-500">{line.note}</span>
                            ) : (
                                <>
                                    {line.oldVal !== undefined && (
                                        <span className="text-red-400 line-through break-all">{String(line.oldVal)}</span>
                                    )}
                                    <span className="text-gray-300">→</span>
                                    {line.newVal !== undefined && (
                                        <span className="text-emerald-600 font-semibold break-all">{String(line.newVal)}</span>
                                    )}
                                </>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {lines.length === 0 && (
                <p className="text-xs text-gray-400 mt-0.5 italic">No field details recorded</p>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
const SheetDetailModal = ({ sheetId, isOpen, onClose }) => {
    const [sheet, setSheet]         = useState(null);
    const [loading, setLoading]     = useState(false);
    const [logo, setLogo]           = useState('axis');
    const [activeTab, setActiveTab] = useState('Sheet');

    // Bug #2 fix: clear sheet before each fetch so stale data never renders
    const fetchSheet = useCallback(async () => {
        if (!sheetId) return;
        setSheet(null);
        setLoading(true);
        try {
            const res = await api.get(`/process-sheets/${sheetId}`);
            setSheet(res.data);
        } catch {
            toast.error('Failed to load sheet details');
        } finally {
            setLoading(false);
        }
    }, [sheetId]);

    useEffect(() => {
        if (isOpen) {
            setActiveTab('Sheet'); // reset to first tab on each open
            fetchSheet();
        }
    }, [isOpen, fetchSheet]);

    if (!isOpen) return null;

    const inputs         = sheet?.inputs || {};
    const bond           = getBondByLabel(inputs.bondType || '');
    const computed       = bond ? computeSheet(inputs, bond) : null;
    const isInProduction = sheet?.status === 'In Production' || sheet?.status === 'Completed';
    const history        = [...(sheet?.updateHistory || [])].reverse(); // newest first

    const handlePrint = () => {
        window.print();
    };

    const tabs = ['Sheet', ...(isInProduction ? ['Items Deducted'] : []), 'History'];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2 sm:p-4 z-[9999]">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] flex flex-col">

                {/* ── Header ──────────────────────────────────────── */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0 no-print">
                    <div className="flex items-center gap-3 min-w-0">
                        <h2 className="text-lg font-bold text-gray-800 flex-shrink-0">
                            {loading ? 'Loading…' : (sheet?.sheetNumber || 'Process Sheet')}
                        </h2>
                        {sheet?.status && (
                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${STATUS_COLORS[sheet.status] || 'bg-gray-100'}`}>
                                {sheet.status}
                            </span>
                        )}
                        {sheet && (
                            <span className="text-xs text-gray-400 truncate">
                                {sheet.clientName}
                                {sheet.inputs?.batchNumber && ` · Batch ${sheet.inputs.batchNumber}`}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {activeTab === 'Sheet' && (
                            <>
                            <LogoToggle value={logo} onChange={setLogo} />
                            <button onClick={handlePrint}
                                className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-xs font-medium transition-colors">
                                🖨 Print
                            </button>
                            </>
                        )}
                        <button onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-1 transition-colors">
                            ×
                        </button>
                    </div>
                </div>

                {/* ── Tabs ────────────────────────────────────────── */}
                {!loading && sheet && (
                    <div className="flex border-b border-gray-100 px-6 no-print flex-shrink-0">
                        {tabs.map(tab => (
                            <button key={tab} onClick={() => setActiveTab(tab)}
                                className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                    activeTab === tab
                                        ? 'border-gray-900 text-gray-900'
                                        : 'border-transparent text-gray-400 hover:text-gray-600'
                                }`}
                            >
                                {tab}
                                {tab === 'History' && history.length > 0 && (
                                    <span className="ml-1.5 text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">
                                        {history.length}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                )}

                {/* ── Body ────────────────────────────────────────── */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading && <p className="text-center text-gray-400 py-12">Loading…</p>}
                    {!loading && !sheet && <p className="text-center text-gray-400 py-12">No data available.</p>}

                    {!loading && sheet && (
                        <>
                            {/* ══ Sheet ══════════════════════════════════ */}
                            {activeTab === 'Sheet' && (
                                <>
                                    {computed ? (
                                        <ProcessSheetTable
                                            inputs={{ ...inputs, customerName: sheet.clientName }}
                                            computed={computed}
                                            sheetNumber={sheet.sheetNumber}
                                            logo={logo}
                                        />
                                    ) : (
                                        <div className="text-center py-8 text-amber-600 text-sm">
                                            ⚠ Bond type "{inputs.bondType}" not found in bond options.
                                        </div>
                                    )}
                                    {!isInProduction && (
                                        <div className="mt-4 bg-gray-50 rounded-lg px-4 py-3 text-xs text-gray-400 border border-gray-100">
                                            Material plan will be recorded when this sheet is sent to production.
                                        </div>
                                    )}
                                </>
                            )}

                            {/* ══ Items Deducted ══════════════════════════ */}
                            {activeTab === 'Items Deducted' && (
                                <div>
                                    <p className="text-xs text-gray-400 mb-4">
                                        Sent by <strong className="text-gray-600">{sheet.sentToProductionBy || '—'}</strong>
                                        {sheet.sentToProductionAt && <> on {fmt(sheet.sentToProductionAt)}</>}
                                    </p>

                                    {sheet.materialPlan?.length > 0 ? (
                                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                                            <table className="w-full text-sm">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Item</th>
                                                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Qty Deducted</th>
                                                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Unit</th>
                                                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Linked</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sheet.materialPlan.map((m, i) => (
                                                        <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                                                            <td className="px-4 py-2.5 font-medium text-gray-800">{m.itemName}</td>
                                                            <td className="px-4 py-2.5 text-right font-mono text-gray-700">{m.quantityUsed}</td>
                                                            <td className="px-4 py-2.5 text-gray-400">{m.unit}</td>
                                                            <td className="px-4 py-2.5">
                                                                {m.itemId
                                                                    ? <span className="text-xs text-emerald-600 font-medium">✓ inventory</span>
                                                                    : <span className="text-xs text-gray-300">—</span>
                                                                }
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-gray-400">No material plan recorded.</p>
                                    )}
                                </div>
                            )}

                            {/* ══ History ═════════════════════════════════ */}
                            {activeTab === 'History' && (
                                <div>
                                    <div className="flex items-center justify-between mb-5">
                                        <h3 className="text-sm font-semibold text-gray-700">Update History</h3>
                                        <span className="text-xs text-gray-400">{history.length} entr{history.length === 1 ? 'y' : 'ies'}</span>
                                    </div>

                                    {history.length === 0 ? (
                                        <p className="text-sm text-gray-400 text-center py-8">No history recorded yet.</p>
                                    ) : (
                                        <div className="ml-2">
                                            {history.map((entry, i) => (
                                                <HistoryEntry
                                                    key={i}
                                                    entry={entry}
                                                    index={i}
                                                    total={history.length}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-400">
                                        Created by <strong>{sheet.createdBy || '—'}</strong> on {fmt(sheet.createdAt)}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SheetDetailModal;